import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { Selo } from '../../componentes/base';
import { Estado, Vazio } from '../../componentes/estados';
import { Drawer } from '../../componentes/sobreposicao';
import { Tabela, type Coluna } from '../../componentes/tabela';
import { get } from '../../nucleo/api';
import { data, mesAno, nomePessoa } from '../../nucleo/formato';
import { horas, type LancamentoBanco, type SaldoBanco } from './tipos';

/** Banco de horas pelos meses fechados: saldo de cada funcionário e empresa, o que vence em 30 dias e os lançamentos. */
export function Banco() {
  const { t } = useTranslation('ponto');
  const [aberto, setAberto] = useState<SaldoBanco | null>(null);
  const colunas: Coluna<SaldoBanco>[] = [
    { chave: 'funcionario_nome', rotulo: t('campos.funcionario'), principal: true, render: (b) => <span className="font-medium">{nomePessoa(b.funcionario_nome) || '-'}</span> },
    { chave: 'empresa_nome', rotulo: t('campos.empresa'), render: (b) => b.empresa_nome ?? '-' },
    { chave: 'saldo_min', rotulo: t('campos.saldo'), alinhar: 'direita', mono: true, render: (b) => <span className={b.saldo_min < 0 ? 'text-perigo' : 'text-tinta-1'}>{horas(b.saldo_min, true)}</span> },
    { chave: 'a_vencer_30_dias_min', rotulo: t('campos.aVencer'), alinhar: 'direita', mono: true, render: (b) => (b.a_vencer_30_dias_min ? horas(b.a_vencer_30_dias_min, true) : '-') },
    { chave: 'proximo_vencimento', rotulo: t('campos.proximoVencimento'), render: (b) => (b.proximo_vencimento ? data(b.proximo_vencimento) : '-') },
    { chave: 'ate_mes', rotulo: t('campos.ateMes'), oculta: true, render: (b) => (b.ate_mes ? mesAno(b.ate_mes) : '-') },
  ];
  return (
    <>
      <Tabela<SaldoBanco> tela="ponto.banco" caminho="/ponto/banco" chaveConsulta={['ponto', 'banco']} colunas={colunas} aoClicar={(b) => setAberto(b)} vazio={<Vazio texto={t('vazio.banco')} />} />
      {aberto && <Lancamentos saldo={aberto} aoFechar={() => setAberto(null)} />}
    </>
  );
}

function Lancamentos({ saldo, aoFechar }: { saldo: SaldoBanco; aoFechar: () => void }) {
  const { t } = useTranslation('ponto');
  const q = useQuery({
    queryKey: ['ponto', 'banco', 'lancamentos', saldo.funcionario_id],
    queryFn: () => get<{ itens: LancamentoBanco[] }>('/ponto/banco/lancamentos', { funcionario_id: saldo.funcionario_id }),
  });
  return (
    <Drawer aberto aoFechar={aoFechar} titulo={`${nomePessoa(saldo.funcionario_nome)} · ${t('campos.saldo')} ${horas(saldo.saldo_min, true)}`}>
      <Estado consulta={q}>
        {(d) =>
          d.itens.length === 0 ? (
            <p className="text-sm text-tinta-3">{t('vazio.lancamentos')}</p>
          ) : (
            <ul className="flex flex-col">
              {d.itens.map((l) => (
                <li key={l.id} className="flex flex-wrap items-center justify-between gap-2 border-b border-borda py-2 text-sm last:border-0">
                  <span className="flex flex-wrap items-center gap-2">
                    <Selo cor={l.natureza === 'credito' ? 'verde' : l.natureza === 'debito' ? 'laranja' : 'neutro'}>{t(`natureza.${l.natureza}`)}</Selo>
                    <span className="text-tinta-2">{mesAno(l.ano_mes)}</span>
                  </span>
                  <span className="flex flex-wrap items-center gap-2">
                    <span className="numero text-tinta-1">{horas(l.minutos, true)}</span>
                    {l.vencimento && <span className="text-xs text-tinta-3">{`${t('campos.vencimento')} ${data(l.vencimento)}`}</span>}
                  </span>
                </li>
              ))}
            </ul>
          )
        }
      </Estado>
    </Drawer>
  );
}
